import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Customers from './pages/Customers';
import RoutesPage from './pages/Routes';
import ServiceMode from './pages/ServiceMode';
import ServiceHistory from './pages/ServiceHistory';
import IntakeForm from './pages/IntakeForm';
import CompanyShowcase from './pages/CompanyShowcase';
import AdminPanel from './pages/AdminPanel';
import Expenses from './pages/Expenses';
import Reports from './pages/Reports';
import Pricing from './pages/Pricing';
import LandingPage from './pages/LandingPage';
import Help from './pages/Help';
import JoinTeam from './pages/JoinTeam';
import MyRoutes from './pages/MyRoutes';
import About from './pages/About';
import Contact from './pages/Contact';

// Portal Pages
import CustomerLogin from './pages/portal/CustomerLogin';
import CustomerPortalSetup from './pages/portal/CustomerPortalSetup';
import CustomerForgotPassword from './pages/portal/CustomerForgotPassword';
import CustomerResetPassword from './pages/portal/CustomerResetPassword';
import CustomerDashboard from './pages/portal/CustomerDashboard';
import CustomerServices from './pages/portal/CustomerServices';
import CustomerInvoices from './pages/portal/CustomerInvoices';
import { PortalAuthProvider } from './context/PortalAuthContext';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Super Admin Route Component
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const { user } = useAuth();

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/dashboard" replace /> : <Register />}
        />
        <Route
          path="/forgot-password"
          element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />}
        />

        {/* Public Pages */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />

        {/* Public Intake Form - accessible without login */}
        <Route path="/new-customer/:companyCode" element={<IntakeForm />} />
        <Route path="/signup/:companyCode" element={<IntakeForm />} />

        {/* Public Company Showcase Page */}
        <Route path="/company/:slug" element={<CompanyShowcase />} />

        {/* Team Invitation - accessible without login */}
        <Route path="/join/:token" element={<JoinTeam />} />

        {/* Customer Portal Routes - wrapped in PortalAuthProvider */}
        <Route path="/portal/login" element={
          <PortalAuthProvider>
            <CustomerLogin />
          </PortalAuthProvider>
        } />
        <Route path="/portal/setup" element={
          <PortalAuthProvider>
            <CustomerPortalSetup />
          </PortalAuthProvider>
        } />
        <Route path="/portal/forgot-password" element={
          <PortalAuthProvider>
            <CustomerForgotPassword />
          </PortalAuthProvider>
        } />
        <Route path="/portal/reset-password" element={
          <PortalAuthProvider>
            <CustomerResetPassword />
          </PortalAuthProvider>
        } />
        <Route path="/portal/:companySlug" element={
          <PortalAuthProvider>
            <CustomerLogin />
          </PortalAuthProvider>
        } />
        <Route path="/portal" element={
          <PortalAuthProvider>
            <CustomerDashboard />
          </PortalAuthProvider>
        } />
        <Route path="/portal/services" element={
          <PortalAuthProvider>
            <CustomerServices />
          </PortalAuthProvider>
        } />
        <Route path="/portal/invoices" element={
          <PortalAuthProvider>
            <CustomerInvoices />
          </PortalAuthProvider>
        } />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <Customers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/routes"
          element={
            <ProtectedRoute>
              <RoutesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <ServiceHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-mode"
          element={
            <ProtectedRoute>
              <ServiceMode />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <Expenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/docs"
          element={
            <ProtectedRoute>
              <Help />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-routes"
          element={
            <ProtectedRoute>
              <MyRoutes />
            </ProtectedRoute>
          }
        />

        {/* Super Admin Routes */}
        <Route
          path="/admin"
          element={
            <SuperAdminRoute>
              <AdminPanel />
            </SuperAdminRoute>
          }
        />

        {/* Default redirect */}
        <Route
          path="/"
          element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />}
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="not-found">
              <h1>404</h1>
              <p>Page not found</p>
              <a href="/">Go home</a>
            </div>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
